/**
 * Fingerprint of the set of row ids a projected list holds. IDENTICAL to functions/src/forms/dataSources/idsHash.ts (cyrb53 over the ids
 * sorted by UTF-16 code unit, joined with NUL); both are pinned by the same golden vector. The phone recomputes it over the list it built
 * by merging a delta and compares with the server's: equal = its list is the server's, unequal = fetch the full list. It guards
 * against accident (a hard delete leaves no updateTime to see, a missed change), not an attacker.
 */
export function idsHash(ids: readonly string[]): string {
  return cyrb53([...ids].sort().join('\u0000'));
}

/**
 * Fingerprint of the CONTENT of a projected list (every cell of every row). IDENTICAL to the server's rowsHash
 * (functions/src/forms/dataSources/idsHash.ts). idsHash sees a row added or removed; this sees a cell that differs under an unchanged
 * key (server code that changed what a column holds without a version bump, a stored copy whose cells were corrupted, a source whose
 * rows can change without their updateTime moving). After merging a delta the phone recomputes this over its list: unequal = fetch the full list.
 * Order independent; each cell JSON-encoded so a null, a number and a string never read alike.
 */
export function rowsHash(rows: ReadonlyArray<ReadonlyArray<string | number | boolean | null>>): string {
  return cyrb53(rows.map((r) => r.map((c) => JSON.stringify(c)).join('\u0001')).sort().join('\u0000'));
}

function cyrb53(text: string): string {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16).padStart(14, '0');
}
